import { ClockCountdown } from "@phosphor-icons/react";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { formatTimestamp } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { ConversationParticipant } from "common/types";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useActiveConversationOwnerAccount from "../../../hooks/useActiveConversationOwnerAccount";
import { stripTextHighlight } from "../../../utils";
import HighlightedText from "../../HighlightedText";

import type { JSX } from "react";
import type { ConversationMessage, MessageSender } from "../../../types";

const groupColors = [
  "text-red-500",
  "text-blue-500",
  "text-purple-500",
  "text-yellow-500",
  "text-green-500",
  "text-orange-500",
  "text-magenta-500",
];

interface MessageBubbleProps {
  isMine: boolean;
  message: ConversationMessage | undefined;
  participants: ConversationParticipant[];
  children: JSX.Element;
  isFirstMessage: boolean;
  emojiReactionsComponent: JSX.Element;
}

export default function MessageBubble({
  isMine,
  message,
  participants,
  children,
  isFirstMessage,
  emojiReactionsComponent,
}: MessageBubbleProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { activeConversation } = useActiveConversationContext();
  const { ownerAccount } = useActiveConversationOwnerAccount();

  let senderColor: string;
  let sender: MessageSender;

  const fullNameHighlight =
    activeConversation.highlight && "full_name" in activeConversation.highlight
      ? activeConversation.highlight.full_name[0]
      : undefined;

  if (message) {
    sender = isMine ? ownerAccount : message.sender;

    if (!isMine && participants.length > 2) {
      const senderIndex = participants.findIndex(
        (participant) => participant.id === sender.id,
      );
      senderColor = groupColors[senderIndex % groupColors.length];
    }
  }

  return (
    <div
      className={clsx([
        "flex flex-row items-center gap-2 group max-w-full",
        isMine ? "flex-row-reverse" : "flex-row",
      ])}
    >
      <div
        className={clsx([
          "flex flex-row gap-2 min-w-0",
          isMine ? "flex-row-reverse" : "flex-row",
        ])}
      >
        <div
          className={clsx([
            "flex w-fit max-w-[332px] flex-col items-start justify-center rounded-3xl p-2 md:max-w-[540px] min-w-0",
            isMine
              ? [
                  message &&
                    !message.deleted &&
                    !message.pending &&
                    "group-hover:bg-purple-100",
                  "bg-purple-50",
                  isFirstMessage && "rounded-tr-none",
                ]
              : [
                  message &&
                    !message.deleted &&
                    !message.pending &&
                    "group-hover:bg-black-200",
                  "bg-black-100",
                  isFirstMessage && "rounded-tl-none",
                ],
          ])}
        >
          {message && sender && (
            <div
              className={clsx([
                "flex w-full flex-row items-center justify-between gap-2",
                isMine ? "mr-auto" : "ml-auto",
              ])}
            >
              <span className={clsx("text-sm-bold", senderColor)}>
                {fullNameHighlight &&
                stripTextHighlight(fullNameHighlight).includes(
                  sender.full_name,
                ) ? (
                  <HighlightedText>{fullNameHighlight}</HighlightedText>
                ) : (
                  sender.full_name
                )}
              </span>

              <div className="flex gap-x-1 items-center">
                <span className="text-xs text-black-500">
                  {formatTimestamp(message.created_at).toUpperCase()}
                  <RenderIf condition={message.edited}>(Edited)</RenderIf>
                </span>
                <RenderIf condition={message.pending}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="xs"
                        variant="ghost-purple"
                        intent="iconOnly"
                      >
                        <ClockCountdown />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      You successfully sent this message. The conversation will
                      sync with Linkedin shortly.
                      <br />
                      <br />
                      Any attachments will load once the conversation is synced
                    </TooltipContent>
                  </Tooltip>
                </RenderIf>
              </div>
            </div>
          )}

          {children}
        </div>
      </div>

      {/* Reactions with default emojis on desktop */}
      <RenderIf condition={isDesktop}>{emojiReactionsComponent}</RenderIf>
    </div>
  );
}
